import PlayerActions from '../enums/PlayerActions';
import PlayerData from '../enums/PlayerData';
import { Networkable } from '../mixins/Networkable';
import { BrowserReloadable } from '../mixins/BrowserReloadable';
import { LogPersistable } from '../mixins/LogPersistable';
import { ApplicationStateQueryable } from '../mixins/ApplicationStateQueryable';
import { Deferred } from '../../Utility/Deferred';
import { addCustomTapListener } from '../../Utility/CustomTapListener';
import { DeviceLog, PlayerResponseData } from '../types';
import { IframeMessengerPlayerAbstract } from '../IframeMessengerPlayerAbstract';
import { LogPersistenceInterface } from '../interfaces/LogPersistenceInterface';
import { NetworkInterface } from '../interfaces/NetworkInterface';
import { ReloadInterface } from '../interfaces/ReloadInterface';
import { ApplicationStateInterface } from '../interfaces/ApplicationStateInterface';
import { DeviceInformationQueryable } from '../mixins/DeviceInformationQueryable';
import { DeviceInformationInterface } from '../interfaces/DeviceInformationInterface';
import { SystemInformationInterface } from '../interfaces/SystemInformationInterface';
import { SystemInformationQueryable } from '../mixins/SystemInformationQueryable';
import { ApplicationRestartable } from '../mixins/ApplicationRestartable';
import { LogInterface } from '../interfaces/LogInterface';
import { isUrlQueryPlatform } from '../PlatformCheck';

/**
 * Player for PWA version of the app
 *
 * @implements module:Platform.NamedValueCacheInterface
 * @implements module:Platform.ScreenshotInterface
 */
class PWAPlayer
    extends DeviceInformationQueryable(
        ApplicationRestartable(
            SystemInformationQueryable(
                ApplicationStateQueryable(
                    LogPersistable(Networkable(BrowserReloadable(IframeMessengerPlayerAbstract)))
                )
            )
        )
    )
    implements
        LogPersistenceInterface,
        LogInterface,
        NetworkInterface,
        ReloadInterface,
        ApplicationStateInterface,
        DeviceInformationInterface,
        SystemInformationInterface
{
    constructor() {
        super(process.env.BASE_URL_PWA as string);
        this.name = 'PWA';
    }

    initializePlatform(): Promise<void> {
        this._setupSettingsListener();
        return Promise.resolve();
    }

    getHostPlatform(): Promise<string> {
        return Promise.resolve(this.name);
    }

    getNamedValue<T extends PlayerResponseData>(key: string, defaultValue: T): Deferred<T> {
        return this._invokePlayerMethod(PlayerActions.GET_NAMED_VALUE, {
            key: key,
            value: defaultValue,
        });
    }

    setNamedValue<T extends PlayerResponseData>(key: string, value: T): Deferred<void> {
        return this._invokePlayerMethod(PlayerActions.SET_NAMED_VALUE, { key, value });
    }

    _generateAndGetHardwareId(): Promise<string> {
        return this.getDeviceInformation().then(function (deviceInfo) {
            return (
                'PWA://' +
                deviceInfo.platform +
                ':' +
                deviceInfo.model +
                ':' +
                deviceInfo.uniqueIdentifier
            );
        });
    }

    deleteNamedValue(key: string): Deferred<void> {
        return this._invokePlayerMethod(PlayerActions.DELETE_NAMED_VALUE, { key });
    }

    getPlayerVersion(): PromiseLike<string> {
        return this._invokePlayerMethod<string>(PlayerActions.GET_PLAYER_VERSION);
    }

    getScreenshot(): Deferred<string> {
        return this._invokePlayerMethod(PlayerActions.GET_SCREENSHOT);
    }

    getPlatformLogs(): Deferred<DeviceLog> {
        return this._getData(PlayerData.PLATFORM_LOGS);
    }

    /**
     * Checks if the device is running PWA
     */
    static isTheRunningPlatform(): boolean {
        const thisPlatformIsRunning = isUrlQueryPlatform('PWA');
        if (typeof thisPlatformIsRunning === 'boolean') {
            return thisPlatformIsRunning;
        }

        // use fallback check
        const displayModes = ['standalone', 'minimal-ui'];
        return displayModes.some(
            (displayMode) => window.matchMedia('(display-mode: ' + displayMode + ')').matches
        );
    }

    /**
     * Opens settings page on triple click of page body
     * We need this fallback in case the device uses touchscreen
     */
    private _setupSettingsListener(): void {
        addCustomTapListener(
            () => this._performPlayerAction(PlayerActions.TOGGLE_SETTINGS_OVERLAY),
            document.getElementsByTagName('body')[0],
            3
        );
    }
}
export { PWAPlayer };
